// Import React hooks
import { useState, useEffect, useCallback, useRef } from "react";



// Exported hook in PascalCase per your convention
export const usePersistentFilters = (

  // A unique identifier for the specific table using this hook
  tableId,

  // The list of filter keys that this table owns
  filterKeys = [],

  // The default filters to apply when nothing exists in URL or localStorage
  defaultFilters = {},

  // Optional configuration object
  options

) => {

  
  // Ref to track whether initialisation has completed
  const initializedRef = useRef(false);


  // Resolve localStorage namespace
  const storageNamespace = (options && options.storageNamespace && options.storageNamespace.trim()) || "filters";


  // Resolve URL param separator
  const urlParamSeparator = ((options && options.urlParamSeparator) ?? ":").trim() || ":";


  // Build the localStorage key using the namespace and the tableId
  const storageKey = `${storageNamespace}:${tableId}`;

  // Check whether this table should store an explicit on/off state for every filter value
  const persistValueStates = options && options.persistValueStates === true;

  // Store explicit filter value states without changing the selected-array API used by the tables
  const valueStatesRef = useRef({});

  // Track where the first filter selection came from so legacy storage can be migrated
  const initialFilterSourceRef = useRef("none");


  // Helper to create a namespaced URL param key like "<tableId>:<key>"
  const toParamKey = useCallback(

    // Return the namespaced key
    (key) => `${tableId}${urlParamSeparator}${key}`,

    // Dependencies
    [tableId, urlParamSeparator]

  );



  // This checks whether a filters object has any selected values in any group
  const hasAnyFilterValues = useCallback((filtersObject) => {

    // Return true if at least one filter group contains one or more values
    return Object.values(filtersObject || {}).some((values) => {

      // Only count non-empty arrays as real selected values
      return Array.isArray(values) && values.length > 0;

    });

  }, []);



  // State for the currently selected filters
  const [selectedFilters, setSelectedFilters] = useState(() => {

    // If window is unavailable, return empty object
    if (typeof window === "undefined") {

      // Return empty object
      return {};

    }

    // Read current URL params
    const params = new URLSearchParams(window.location.search);

    // Prepare an object to hold filters parsed from URL
    const initialFromUrl = {};

    // If specific keys were provided, read only those
    if (Array.isArray(filterKeys) && filterKeys.length > 0) {

      // Loop over each declared key
      filterKeys.forEach((key) => {

        // Build namespaced param name
        const paramName = toParamKey(key);

        // Read raw value from URL
        const raw = params.get(paramName);

        // Only add the key when a real value exists
        if (raw) {

          // Split the raw value into an array
          initialFromUrl[key] = raw.split(",");

        }

      });

    } else {

      // If no keys were provided, scan URL for this table's namespaced params
      const prefix = `${tableId}${urlParamSeparator}`;

      // Iterate all URL params
      params.forEach((_value, name) => {

        // Only process params that belong to this table
        if (name.startsWith(prefix)) {

          // Extract the actual key after the prefix
          const key = name.slice(prefix.length);

          // Read the raw value for the param
          const raw = params.get(name);

          // Only add the key when a real value exists
          if (raw) {

            // Split the raw value into an array
            initialFromUrl[key] = raw.split(",");

          }

        }

      });

    }

    // Check whether URL contributed any real selected values
    const urlHasValues = Object.values(initialFromUrl).some((values) => {

      // Only count non-empty arrays
      return Array.isArray(values) && values.length > 0;

    });

    // If URL has values, prefer those
    if (urlHasValues) {

      // Record that the initial selection came from the URL
      initialFilterSourceRef.current = "url";

      // Return filters from URL
      return initialFromUrl;

    }

    // Otherwise try localStorage
    try {

      // Read the table-scoped entry
      const stored = localStorage.getItem(storageKey);

      // Parse stored filters if present
      const parsedStored = stored ? JSON.parse(stored) : null;

      // Check for the explicit per-value state format
      if (
        persistValueStates &&
        parsedStored &&
        typeof parsedStored === "object" &&
        parsedStored.valueStates &&
        typeof parsedStored.valueStates === "object"
      ) {

        // Store the saved value states for later reconciliation with the available options
        valueStatesRef.current = parsedStored.valueStates;

        // Record that explicit value states were loaded
        initialFilterSourceRef.current = "states";

        // Prepare the selected arrays expected by existing table components
        const storedSelectedFilters = {};

        // Loop through each stored filter group
        Object.entries(parsedStored.valueStates).forEach(([key, states]) => {

          // Ignore invalid filter groups
          if (!states || typeof states !== "object" || Array.isArray(states)) return;

          // Include each value whose saved state is on
          storedSelectedFilters[key] = Object.entries(states)
            .filter(([_value, enabled]) => enabled === true)
            .map(([value]) => value);

        });

        // Return the selected values rebuilt from their saved states
        return storedSelectedFilters;

      }

      // Treat the old selected-array format as legacy data for tables now using value states
      if (
        persistValueStates &&
        parsedStored &&
        typeof parsedStored === "object" &&
        !Array.isArray(parsedStored)
      ) {

        // Record that legacy filter data was loaded
        initialFilterSourceRef.current = "legacy";

        // Return the old selected arrays for the first render
        return parsedStored;

      }

      // If localStorage has real filter values, use them
      if (
        parsedStored &&
        typeof parsedStored === "object" &&
        Object.values(parsedStored).some((values) => Array.isArray(values) && values.length > 0)
      ) {

        // Return stored filters
        return parsedStored;

      }

    } catch (e) {

      // Ignore parse and storage errors
    }

    // If defaults have real values, use them
    if (
      defaultFilters &&
      typeof defaultFilters === "object" &&
      Object.values(defaultFilters).some((values) => Array.isArray(values) && values.length > 0)
    ) {

      // Return default filters
      return defaultFilters;

    }

    // Final fallback
    return {};

  });



  // This completes initialisation once defaults are available if needed
  useEffect(() => {

    // Stop if initialisation has already completed
    if (initializedRef.current) {

      // Exit early
      return;

    }

    // Stored state data counts as initialised even when every saved value is switched off
    if (persistValueStates && initialFilterSourceRef.current !== "none") {

      // Mark as initialised
      initializedRef.current = true;

      // Exit early
      return;

    }

    // Check whether selected filters already contain real values
    const hasSelectedFilters = hasAnyFilterValues(selectedFilters);

    // If selected filters already exist, mark initialised and stop
    if (hasSelectedFilters) {

      // Mark as initialised
      initializedRef.current = true;

      // Exit early
      return;

    }

    // Stop until default filters are available
    if (!defaultFilters || Object.keys(defaultFilters).length === 0) {

      // Exit early
      return;

    }

    // Check whether default filters contain real values
    const hasDefaultValues = hasAnyFilterValues(defaultFilters);

    // If defaults contain real values, apply them
    if (hasDefaultValues) {

      // Apply the provided default filters
      setSelectedFilters(defaultFilters);

    }

    // Mark initialisation as complete
    initializedRef.current = true;

  }, [defaultFilters, selectedFilters, hasAnyFilterValues, persistValueStates]);

  // Reconcile saved value states with the filter values currently available to the table
  useEffect(() => {

    // Stop if this table is using the original selected-array persistence mode
    if (!persistValueStates) return;

    // Stop until filter options are available
    if (!defaultFilters || Object.keys(defaultFilters).length === 0) return;

    // Update the selected arrays when stored or newly discovered values require it
    setSelectedFilters((previousFilters) => {

      // Copy the current filter selection
      const nextFilters = {
        ...previousFilters,
      };

      // Track whether either the saved states or selected arrays change
      let stateChanged = false;
      let filtersChanged = false;

      // Loop through each available filter group
      Object.entries(defaultFilters).forEach(([key, values]) => {

        // Ignore invalid filter groups
        if (!Array.isArray(values)) return;

        // Create the saved state group when it does not exist yet
        if (!valueStatesRef.current[key] || typeof valueStatesRef.current[key] !== "object") {
          valueStatesRef.current[key] = {};
        }

        // Copy the values currently selected in this group
        const nextValues = Array.isArray(previousFilters[key]) ? [...previousFilters[key]] : [];

        // Track whether this group's selected values change
        let groupChanged = false;

        // Loop through each value currently available to this filter group
        values.forEach((value) => {

          // Check whether this value already has a saved state
          const hasSavedState = Object.prototype.hasOwnProperty.call(valueStatesRef.current[key], value);

          // New values default to on, except during the first reconciliation of an explicit URL filter
          if (!hasSavedState) {

            // Preserve the initial URL selection; otherwise a genuinely new value starts on
            const enabled = initialFilterSourceRef.current === "url" ? nextValues.includes(value) : true;

            // Store the new value state
            valueStatesRef.current[key][value] = enabled;
            stateChanged = true;

          }

          // Read the resolved saved state
          const enabled = valueStatesRef.current[key][value] === true;

          // Add enabled values that are not selected yet
          if (enabled && !nextValues.includes(value)) {
            nextValues.push(value);
            groupChanged = true;
            filtersChanged = true;
          }

          // Remove disabled values that are still selected
          if (!enabled && nextValues.includes(value)) {
            nextValues.splice(nextValues.indexOf(value), 1);
            groupChanged = true;
            filtersChanged = true;
          }

        });

        // Store the updated selected values for this group
        if (groupChanged) nextFilters[key] = nextValues;

      });

      // Future values with no saved state are genuinely new and should default to on
      initialFilterSourceRef.current = "states";

      // Trigger persistence when either the state map or selected arrays changed
      return stateChanged || filtersChanged ? nextFilters : previousFilters;

    });

  }, [defaultFilters, persistValueStates]);

  // Effect to persist to localStorage and sync URL
  useEffect(() => {

    // Do not persist until initialization has completed
    if (!initializedRef.current) {

      // Exit early
      return;

    }

    // Guard for non-browser environments
    if (typeof window === "undefined") {

      // Exit early
      return;

    }

    // Persist to localStorage under this table's key
    try {

      // Keep explicit value states in sync with the selected arrays
      if (persistValueStates) {

        // Loop through each known filter group
        Object.entries(valueStatesRef.current).forEach(([key, states]) => {

          // Ignore invalid filter groups
          if (!states || typeof states !== "object" || Array.isArray(states)) return;

          // Get the currently selected values in this group
          const selectedValues = Array.isArray(selectedFilters[key]) ? selectedFilters[key] : [];

          // Loop through each known value in this group
          Object.keys(states).forEach((value) => {
            states[value] = selectedValues.includes(value);
          });

        });

      }

      // Serialize explicit value states or the original selected arrays
      const serialized = persistValueStates
        ? JSON.stringify({ valueStates: valueStatesRef.current })
        : JSON.stringify(selectedFilters);

      // Store safely
      localStorage.setItem(storageKey, serialized);

    } catch (e) {

      // Ignore storage errors
    }

    // Start with current search params so we preserve unrelated query params
    const params = new URLSearchParams(window.location.search);

    // Compute the prefix for this table
    const prefix = `${tableId}${urlParamSeparator}`;

    // Collect all param names that belong to this table
    const namesToDelete = [];

    // Iterate all params to find table-scoped ones
    params.forEach((_value, name) => {

      // If the param belongs to this table, mark it for deletion
      if (name.startsWith(prefix)) {

        // Add the name to the deletion list
        namesToDelete.push(name);

      }

    });

    // Remove old table-scoped params so we can write fresh ones
    namesToDelete.forEach((name) => {

      // Delete the old param
      params.delete(name);

    });

    // Write the current selection back under the table's namespace
    Object.entries(selectedFilters || {}).forEach(([key, values]) => {

      // Only write non-empty arrays
      if (Array.isArray(values) && values.length > 0) {

        // Build namespaced param name
        const paramName = toParamKey(key);

        // Join array values by comma
        params.set(paramName, values.join(","));

      }

    });

    // Build the query string
    const queryString = params.toString();

    // Build the new URL while preserving unrelated query params
    const newUrl = queryString
      ? `${window.location.pathname}?${queryString}`
      : window.location.pathname;

    // Replace the current history entry to avoid pushing a new one
    window.history.replaceState(null, "", newUrl);

  }, [selectedFilters, storageKey, tableId, urlParamSeparator, toParamKey, persistValueStates]);



  // This toggles a single filter value on or off
  const toggleFilterValue = useCallback((key, value) => {

    // Update the filters state
    setSelectedFilters((previousFilters) => {

      // Get the current included values for this group
      const currentValues = previousFilters[key] || [];

      // Check whether the value is already included
      const isAlreadySelected = currentValues.includes(value);

      // Return the updated filter state
      return {
        ...previousFilters,
        [key]: isAlreadySelected
          ? currentValues.filter((item) => item !== value)
          : [...currentValues, value],
      };

    });

  }, []);



  // This toggles all values in a group on or off depending on whether all are selected
  const toggleSelectAllFilters = useCallback((key, allValues = []) => {

    // Update the filters state
    setSelectedFilters((previousFilters) => {

      // Get the current included values for this group
      const currentValues = previousFilters[key] || [];

      // Check whether all values are already selected
      const isEverythingSelected = currentValues.length === allValues.length;

      // Return the updated filter state
      return {
        ...previousFilters,
        [key]: isEverythingSelected ? [] : [...allValues],
      };

    });

  }, []);



  // This checks whether all values are selected for a filter group
  const isAllSelected = useCallback((key, allValues = []) => {

    // Read selected values or empty array
    const selectedValues = selectedFilters[key] || [];

    // Return true when lengths match
    return selectedValues.length === allValues.length;

  }, [selectedFilters]);



  // This clears a single filter group
  const clearFilterGroup = useCallback((key) => {

    // Update by merging with an empty array for this key
    setSelectedFilters((previousFilters) => ({

      // Keep existing groups
      ...previousFilters,

      // Clear this group
      [key]: [],

    }));

  }, []);



  // This clears all filters for this table
  const clearAllFilters = useCallback(() => {

    // Reset the table's filter state
    setSelectedFilters({});

  }, []);



  // This includes every value in a filter group
  const setFilterGroupAllOn = useCallback((key, values = []) => {

    // Update the filters state
    setSelectedFilters((previousFilters) => {

      // Return the updated filter state
      return {
        ...previousFilters,
        [key]: [...values],
      };

    });

  }, []);



  // This excludes every value in a filter group
  const setFilterGroupAllOff = useCallback((key) => {

    // Update the filters state
    setSelectedFilters((previousFilters) => {

      // Return the updated filter state
      return {
        ...previousFilters,
        [key]: [],
      };

    });

  }, []);



  // This resets a filter group back to its default values
  const resetFilterGroup = useCallback((key) => {

    // Update the filters state
    setSelectedFilters((previousFilters) => {

      // Return the updated filter state
      return {
        ...previousFilters,
        [key]: [...(defaultFilters[key] || [])],
      };

    });

  }, [defaultFilters]);





  



  // Public API returned by the hook
  return {

    // Current selection object
    selectedFilters,

    // Function to update the selection
    setSelectedFilters,

    // Toggle a single value inside a filter key
    toggleFilterValue,

    // Toggle all values for a key
    toggleSelectAllFilters,

    // Check if all values are selected for a key
    isAllSelected,

    // Clear one filter key
    clearFilterGroup,

    // Clear all filter keys for this table
    clearAllFilters,

    // Helpers for toggle-switch based filter groups
    setFilterGroupAllOn,
    setFilterGroupAllOff,
    resetFilterGroup,

  };

};